import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, timeout: 30000 });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Track if a refresh is in progress to avoid loops
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Skip interceptor for login, refresh, and public routes
    const isAuthRoute = originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/refresh');
    const isPublicPage = window.location.pathname.includes('/login') || window.location.pathname.includes('/public/');
    
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthRoute && !isPublicPage) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(() => Promise.reject(error));
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      const token = localStorage.getItem('token');
      if (!token) {
        isRefreshing = false;
        processQueue(new Error('No token'), null);
        localStorage.removeItem('token');
        window.location.href = '/login?expired=1';
        return Promise.reject(error);
      }
      
      try {
        const res = await axios.post(`${API}/auth/refresh`, {}, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        });
        const newToken = res.data.access_token;
        localStorage.setItem('token', newToken);
        isRefreshing = false;
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        processQueue(refreshError, null);
        localStorage.removeItem('token');
        window.location.href = '/login?expired=1';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');

// Leads
export const getLeads = (params = {}) => api.get('/leads', { params });
export const getLead = (id) => api.get(`/leads/${id}`);
export const markAllLeadsSeen = () => api.post('/leads/mark-all-seen');
export const updateLead = (id, data) => api.put(`/leads/${id}`, data);
export const convertLeadToQuote = (id) => api.post(`/leads/${id}/convert-to-quote`);
export const getLeadPricing = (id) => api.get(`/leads/pricing/${id}`);
export const approveLead = (id, data) => api.post(`/leads/${id}/approve`, data);

// Quotes (main entity — replaces leads)
export const getQuotes = (params = {}) => api.get('/quotes', { params });
export const getQuotesAgents = () => api.get('/quotes/agents/list');
export const getQuote = (id) => api.get(`/quotes/${id}`);
export const createQuote = (data) => api.post('/quotes', data);
export const updateQuote = (id, data) => api.put(`/quotes/${id}`, data);
export const deleteQuote = (id) => api.delete(`/quotes/${id}`);
export const convertQuoteToOrder = (id) => api.post(`/quotes/${id}/convert-to-order`);
export const generateInvoiceFromQuote = (quoteId, invoiceType = 'customer') =>
  api.post(`/quotes/${quoteId}/generate-invoice?invoice_type=${invoiceType}`);
export const generateAgreementFromQuote = (quoteId, agreementType = 'transport') =>
  api.post(`/quotes/${quoteId}/generate-agreement?agreement_type=${agreementType}`);

// Orders
export const getOrders = (params = {}) => api.get('/orders', { params });
export const getOrder = (id) => api.get(`/orders/${id}`);
export const updateOrder = (id, data) => api.put(`/orders/${id}`, data);
export const deleteOrder = (id) => api.delete(`/orders/${id}`);
export const createOrderDirect = (data) => api.post('/orders/direct', data);

// Carriers
export const getCarriers = () => api.get('/carriers');
export const getCarrier = (id) => api.get(`/carriers/${id}`);
export const createCarrier = (data) => api.post('/carriers', data);
export const updateCarrier = (id, data) => api.put(`/carriers/${id}`, data);
export const deleteCarrier = (id) => api.delete(`/carriers/${id}`);

// Invoices
export const getInvoices = (params = {}) => api.get('/invoices', { params });
export const getInvoice = (id) => api.get(`/invoices/${id}`);
export const createInvoice = (data) => api.post('/invoices', data);
export const updateInvoice = (id, data) => api.put(`/invoices/${id}`, data);
export const deleteInvoice = (id) => api.delete(`/invoices/${id}`);

// Users
export const getUsers = () => api.get('/users');
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);
export const registerUser = (data) => api.post('/auth/register', data);
export const refreshToken = () => api.post('/auth/refresh');

// Phone Migration (Super Admin)
export const migratePhones = () => api.post('/admin/migrate-phones');

// Phone formatting utility
export const formatPhoneE164 = (phone) => {
  if (!phone || !phone.trim()) return { formatted: '', valid: true };
  const digits = phone.replace(/[^\d]/g, '');
  if (phone.trim().startsWith('+')) {
    if (digits.length === 11 && digits.startsWith('1')) return { formatted: `+${digits}`, valid: true };
    if (digits.length === 10) return { formatted: `+1${digits}`, valid: true };
    return { formatted: `+${digits}`, valid: digits.length >= 10 };
  }
  if (digits.length === 10) return { formatted: `+1${digits}`, valid: true };
  if (digits.length === 11 && digits.startsWith('1')) return { formatted: `+${digits}`, valid: true };
  return { formatted: phone.trim(), valid: false };
};

// Settings
export const getCompanySettings = () => api.get('/settings/company');
export const updateCompanySettings = (data) => api.put('/settings/company', data);

// Chat
export const getChatChannels = () => api.get('/chat/channels');
export const getChatMessages = (channel, limit = 100) => api.get('/chat/messages', { params: { channel, limit } });
export const sendChatMessage = (data) => api.post('/chat/send', data);
export const markChatRead = (channel) => api.post('/chat/read', null, { params: { channel } });
export const uploadChatFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/chat/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const createChatGroup = (data) => api.post('/chat/groups', data);
export const getChatGroups = () => api.get('/chat/groups');
export const searchChatUsers = (q = '') => api.get('/chat/users/search', { params: { q } });

// Admin - API Management
export const getVendorApiKey = () => api.get('/leads/api-key');
export const regenerateVendorApiKey = () => api.post('/leads/api-key/regenerate');
export const getApiLogs = (limit = 100) => api.get('/admin/api-logs', { params: { limit } });
export const getLeadSources = () => api.get('/admin/lead-sources');

// Admin - Lead Distribution
export const getDistributionRules = () => api.get('/admin/distribution');
export const upsertDistributionRule = (agent_name, source, weight = 1, enabled = true) =>
  api.post('/admin/distribution', null, { params: { agent_name, source, weight, enabled } });
export const deleteDistributionRule = (agent_name, source) =>
  api.delete('/admin/distribution', { params: { agent_name, source } });

// Agreements
export const getAgreements = (params = {}) => api.get('/agreements', { params });
export const getAgreement = (id) => api.get(`/agreements/${id}`);
export const createAgreement = (data) => api.post('/agreements', data);
export const updateAgreement = (id, data) => api.put(`/agreements/${id}`, data);
export const sendAgreement = (id) => api.post(`/agreements/${id}/send`);
export const signAgreement = (id, data) => api.post(`/agreements/${id}/sign`, data);
export const voidAgreement = (id) => api.post(`/agreements/${id}/void`);
export const deleteAgreement = (id) => api.delete(`/agreements/${id}`);

// Revenue
export const submitRevenueForm = (data) => api.post('/revenue', data);
export const getRevenueForms = (params = {}) => api.get('/revenue', { params });
export const getRevenueByOrder = (orderId) => api.get(`/revenue/by-order/${orderId}`);
export const getRevenueAdminSummary = (month) => api.get('/revenue/admin/summary', { params: month ? { month } : {} });
export const updateRevenueForm = (id, data) => api.put(`/revenue/${id}`, data);
export const deleteRevenueForm = (id) => api.delete(`/revenue/${id}`);
export const getRevenueMonthlyHistory = (params = {}) => api.get('/revenue/monthly-history', { params });

// Generate Invoice from Order
export const generateInvoiceFromOrder = (orderId, invoiceType = 'customer') =>
  api.post(`/orders/${orderId}/generate-invoice?invoice_type=${invoiceType}`);
export const signInvoice = (invoiceId, data) => api.post(`/invoices/${invoiceId}/sign`, data);

// Lead Email Settings
export const getLeadEmail = () => api.get('/settings/lead-email');
export const regenerateLeadEmail = () => api.post('/settings/lead-email/regenerate');

// Reminders
export const createReminder = (data) => api.post('/reminders', data);
export const getReminders = (params = {}) => api.get('/reminders', { params });
export const getTodayReminders = () => api.get('/reminders/today');
export const updateReminder = (id, data) => api.put(`/reminders/${id}`, data);
export const deleteReminder = (id) => api.delete(`/reminders/${id}`);

// Sidebar badge counts
export const getSidebarCounts = () => api.get('/sidebar/counts');

// Lead Assignment (Super Admin / Admin)
export const assignLead = (leadId, data) => api.put(`/leads/${leadId}/assign`, data);
export const bulkAssignLeads = (data) => api.post('/leads/bulk-assign', data);
export const redistributeLeads = () => api.post('/leads/redistribute-unassigned');
export const reassignAllLeadsToMe = () => api.post('/leads/reassign-all-to-me');

// Revenue Targets (Super Admin)
export const getRevenueTargets = () => api.get('/admin/revenue-targets');
export const updateRevenueTarget = (userId, amount) => api.put(`/admin/revenue-targets/${userId}`, null, { params: { amount } });
export const updateTeamRevenueTarget = (amount) => api.put('/admin/team-revenue-target', null, { params: { amount } });

// Email Config & Templates (Super Admin)
export const getEmailConfig = () => api.get('/settings/email-config');
export const updateEmailConfig = (data) => api.put('/settings/email-config', data);
export const getEmailTemplates = () => api.get('/settings/email-templates');
export const updateEmailTemplate = (id, data) => api.put(`/settings/email-templates/${id}`, data);
export const previewEmailTemplate = (id) => api.post(`/settings/email-templates/${id}/preview`);
export const testSendEmailTemplate = (id, toEmail) => api.post(`/settings/email-templates/${id}/test-send`, null, { params: { to_email: toEmail } });
export const getEmailLogs = (limit = 50) => api.get('/settings/email-logs', { params: { limit } });

// Compliance APIs (Phase 6)
export const getComplianceDashboard = () => api.get('/compliance/dashboard');
export const getDncList = (params = {}) => api.get('/compliance/dnc', { params });
export const addToDnc = (data) => api.post('/compliance/dnc', data);
export const bulkAddDnc = (data) => api.post('/compliance/dnc/bulk', data);
export const removeFromDnc = (id) => api.delete(`/compliance/dnc/${id}`);
export const checkDnc = (phone) => api.get(`/compliance/dnc/check/${encodeURIComponent(phone)}`);
export const getCallingHours = () => api.get('/compliance/calling-hours');
export const updateCallingHours = (data) => api.put('/compliance/calling-hours', data);
export const checkCallingHours = (state) => api.post('/compliance/calling-hours/check', null, { params: { state } });
export const checkCallingHoursForLead = (leadId) => api.post(`/compliance/calling-hours/check-lead/${leadId}`);
export const getOptOuts = (params = {}) => api.get('/compliance/opt-outs', { params });
export const markOptOut = (leadId) => api.post(`/compliance/opt-out/${leadId}`);
export const removeOptOut = (leadId) => api.delete(`/compliance/opt-out/${leadId}`);
export const getAuditLogs = (params = {}) => api.get('/compliance/audit-logs', { params });
export const getAuditActions = () => api.get('/compliance/audit-logs/actions');
export const getAuditStats = () => api.get('/compliance/audit-logs/stats');

export default api;
